import { Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, BehaviorSubject, of } from 'rxjs';
import { map, take, takeUntil, catchError } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { GqlErrorService } from '../shared/services/gql-error.service';
import { pickerModuleInjector } from '../shared/utils/picker-module.util';
import { RoleNames, UserTypesPlural, UserTypes, RoutePaths, BusinessRulesLocation } from '../shared/api/types/enums';
import { CreateCompanyInput, CreateContactInput, CustomerInput } from '../shared/api/types/GraphQL';
import { LanguageService } from '../shared/services/language.service';

export interface QuickAction {
  id: string;
  title: string;
  // Material icon name rendered next to the action label in the redesigned
  // quick-create popup. Optional so legacy callers still compile.
  icon?: string;
  // Logical group for visual separators. Adjacent rows sharing a group sit
  // together; a divider is rendered when the group changes between siblings.
  group?: string;
  defaultEntityName?: string;
  access?: RoleNames | RoleNames[];
  selectedAction?: UserTypesPlural;
}

@Injectable({
  providedIn: 'root'
})
export class QuickActionsService implements OnDestroy {
  private destroySubject = new Subject<void>();
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private ruleType: BusinessRulesLocation = BusinessRulesLocation.incentives;

  // Default quick actions configuration
  private readonly defaultQuickActions: QuickAction[] = [
    {
      id: 'newProduct',
      title: 'quick_actions.new_product',
      icon: 'inventory_2',
      group: 'catalog',
      access: [RoleNames.backoffice, RoleNames.product]
    },
    {
      id: 'newCategory',
      title: 'quick_actions.new_category',
      icon: 'category',
      group: 'catalog',
      access: [RoleNames.backoffice, RoleNames.product]
    },
    {
      id: 'newOrderDropshipment',
      title: 'quick_actions.new_order',
      icon: 'receipt_long',
      group: 'orders',
      access: RoleNames.order
    },
    {
      id: 'newOrderQuotation',
      title: 'quick_actions.new_quote',
      icon: 'description',
      group: 'orders',
      access: [RoleNames.sales, RoleNames.order]
    },
    {
      id: 'newCompany',
      title: 'quick_actions.new_company',
      icon: 'apartment',
      group: 'crm',
      defaultEntityName: 'New company',
      selectedAction: UserTypesPlural.companies,
      access: [RoleNames.backoffice, RoleNames.user]
    },
    {
      id: 'newContact',
      title: 'quick_actions.new_contact',
      icon: 'contact_page',
      group: 'crm',
      defaultEntityName: 'New contact',
      selectedAction: UserTypesPlural.contacts,
      access: [RoleNames.backoffice, RoleNames.user]
    },
    {
      id: 'newCustomer',
      title: 'quick_actions.new_customer',
      icon: 'person',
      group: 'crm',
      defaultEntityName: 'New customer',
      selectedAction: UserTypesPlural.customers,
      access: [RoleNames.backoffice, RoleNames.user]
    },
    {
      id: 'newRule',
      title: 'quick_actions.new_incentive',
      icon: 'local_offer',
      group: 'pricing',
      // Mirrors the backend (NBO-2446): businessRuleCreate is configuration OWNER, for every rule
      // type. A list here is OR-ed by both directives, so the role stands alone.
      access: RoleNames.configuration
    },
    {
      id: 'newBackofficeUser',
      title: 'quick_actions.new_backoffice_user',
      icon: 'admin_panel_settings',
      group: 'admin',
      access: [RoleNames.backoffice, RoleNames.role]
    }
  ];

  private quickActionsSubject = new BehaviorSubject<QuickAction[]>(this.defaultQuickActions);

  constructor(
    private router: Router,
    private injector: Injector,
    private errorService: GqlErrorService,
    private dialog: MatDialog,
    private languageService: LanguageService
  ) {}

  // Public observables
  get quickActions$(): Observable<QuickAction[]> {
    return this.quickActionsSubject.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  /**
   * Get filtered actions based on user roles
   * @param userRoles Array of user roles
   * @returns Observable of filtered quick actions
   */
  getFilteredActions(userRoles: RoleNames[]): Observable<QuickAction[]> {
    return this.quickActions$.pipe(map((actions) => actions.filter((action) => this.hasAccess(action, userRoles))));
  }

  /**
   * Execute a quick action by its ID
   * @param actionId The ID of the action to execute
   */
  executeAction(actionId: string): void {
    try {
      const actionMap = new Map<string, () => void>([
        ['newProduct', () => this.createNewProduct()],
        ['newCategory', () => this.createNewCategory()],
        ['newOrderDropshipment', () => this.createNewOrder('dropshipment')],
        ['newOrderQuotation', () => this.createNewOrder('quotation')],
        ['newCompany', () => this.createNewUser(UserTypesPlural.companies)],
        ['newContact', () => this.createNewUser(UserTypesPlural.contacts)],
        ['newCustomer', () => this.createNewUser(UserTypesPlural.customers)],
        ['newRule', () => this.createNewRule()],
        ['newBackofficeUser', () => this.createNewBackofficeUser()]
      ]);

      const actionFunction = actionMap.get(actionId);
      if (actionFunction) {
        actionFunction();
      } else {
        console.warn(`Unknown action ID: ${actionId}`);
        this.errorService.getGqlError(new Error(`Unknown action ID: ${actionId}`));
      }
    } catch (error) {
      console.error('Error executing action:', error);
      this.errorService.getGqlError(error);
      this.setLoading(false); // Ensure loading state is reset on error
    }
  }

  /**
   * Create a new product
   */
  createNewProduct(): void {
    this.setLoading(true);
    import('../pim/products/products.service').then(({ ProductsService }) => {
      try {
        const productsService = this.injector.get(ProductsService);
        productsService
          .productCreate({
            names: this.languageService.buildPlaceholderLocalizedStrings('new_product'),
            orderableFrom: null,
            orderableTo: null
          })
          .pipe(
            map((data) => data.data.productCreate),
            takeUntil(this.destroySubject),
            catchError((error) => {
              console.error('GraphQL error creating product:', error);
              this.errorService.getGqlError(error);
              this.setLoading(false);
              return of(null);
            })
          )
          .subscribe({
            next: (product) => {
              if (product) {
                this.openProductDetailPage(product);
              }
              this.setLoading(false);
            },
            error: (error) => {
              console.error('Unexpected error creating product:', error);
              this.errorService.getGqlError(error);
              this.setLoading(false);
            }
          });
      } catch (error) {
        console.error('Error creating new product:', error);
        this.errorService.getGqlError(error);
        this.setLoading(false);
      }
    });
  }

  /**
   * Create a new user (company, contact, or customer)
   * @param userType The type of user to create
   */
  createNewUser(userType: UserTypesPlural): void {
    try {
      const action = this.defaultQuickActions.find((a) => a.selectedAction === userType);
      if (!action) {
        console.error(`Unknown user type: ${userType}`);
        return;
      }

      if (userType === UserTypesPlural.contacts) {
        this.openCompanySelectionDialog(action);
      } else {
        // Default parent ID - this should be configurable
        this.createUserEntity(action);
      }
    } catch (error) {
      console.error('Error creating new user:', error);
      this.errorService.getGqlError(error);
    }
  }

  /**
   * Create a new order
   * @param orderType The type of order ('dropshipment' or 'quotation')
   */
  createNewOrder(orderType: string): void {
    try {
      let url: string;

      if (orderType === 'dropshipment') {
        url = this.router.serializeUrl(this.router.createUrlTree([RoutePaths.OrdersSpNew]));
      } else if (orderType === 'quotation') {
        url = this.router.serializeUrl(this.router.createUrlTree([RoutePaths.QuotesSpNew]));
      } else {
        console.error(`Unknown order type: ${orderType}`);
        return;
      }

      window.open(url, '_blank');
    } catch (error) {
      console.error('Error creating new order:', error);
    }
  }

  /**
   * Navigate to create new category
   */
  createNewCategory(): void {
    try {
      this.router.navigateByUrl(RoutePaths.PimCategories);
    } catch (error) {
      console.error('Error navigating to categories:', error);
    }
  }

  /**
   * Navigate to create new rule
   */
  createNewRule(): void {
    try {
      this.router.navigate(['rule-builder', this.ruleType, 'new']);
    } catch (error) {
      console.error('Error navigating to rule builder:', error);
    }
  }

  /**
   * Navigate to create new backoffice user
   */
  createNewBackofficeUser(): void {
    try {
      this.router.navigate([`${RoutePaths.AdminBackofficeUsers}/new`]);
    } catch (error) {
      console.error('Error navigating to backoffice users:', error);
    }
  }

  /**
   * Check if user has access to a specific action
   * @param action The action to check
   * @param userRoles Array of user roles
   * @returns True if user has access
   */
  private hasAccess(action: QuickAction, userRoles: RoleNames[]): boolean {
    if (!action.access) {
      return true; // No access restriction
    }

    const requiredRoles = Array.isArray(action.access) ? action.access : [action.access];
    return requiredRoles.some((role) => userRoles.includes(role));
  }

  /**
   * Open product detail page
   * @param product The created product
   */
  private openProductDetailPage(product: any): void {
    try {
      const resolvedName = this.languageService.resolveLocalizedString(product?.names);
      if (resolvedName) {
        const productName = resolvedName.replaceAll(/ |\/|\ - |\./g, '-').replace(/[(),]/g, '');
        this.router.navigate(['pim/products-and-clusters/product/' + product.productId + '/' + productName]);
      } else {
        this.router.navigate(['pim/products-and-clusters/product/' + product.productId]);
      }
    } catch (error) {
      console.error('Error opening product detail page:', error);
    }
  }

  /**
   * Create user entity
   * @param parentId The parent ID for the user
   * @param action The action configuration
   */
  private createUserEntity(action: QuickAction, parentId?: number): void {
    this.setLoading(true);
    import('../crm/users/users.service').then(({ UsersService }) => {
      try {
        const usersService = this.injector.get(UsersService);
        const defaultName = action.defaultEntityName;
        const newEntity: CreateCompanyInput | CreateContactInput | CustomerInput = {
          name: action.selectedAction === UserTypesPlural.companies ? defaultName : undefined,
          firstName: action.selectedAction !== UserTypesPlural.companies ? defaultName : undefined,
          lastName: action.selectedAction !== UserTypesPlural.companies ? '' : undefined,
          parentId: parentId || undefined
        };

        const userType =
          action.selectedAction === UserTypesPlural.companies
            ? UserTypes.company
            : (action.selectedAction!.slice(0, -1) as UserTypes);

        usersService
          .createUser(action.selectedAction!, newEntity)
          .pipe(
            map((data) => data.data[`${userType}Create`]),
            takeUntil(this.destroySubject),
            catchError((error) => {
              console.error('GraphQL error creating user:', error);
              this.errorService.getGqlError(error);
              this.setLoading(false);
              return of(null);
            })
          )
          .subscribe({
            next: (user) => {
              if (user) {
                const userId = user[`${userType}Id`];
                this.router.navigateByUrl(`${RoutePaths.UserManagementUsersDetails}/${userType}/${userId}`);
              }
              this.setLoading(false);
            },
            error: (error) => {
              console.error('Unexpected error creating user:', error);
              this.errorService.getGqlError(error);
              this.setLoading(false);
            }
          });
      } catch (error) {
        console.error('Error creating user entity:', error);
        this.setLoading(false);
      }
    });
  }

  /**
   * Open company selection dialog for contact creation
   * @param action The action configuration
   */
  private openCompanySelectionDialog(action: QuickAction): void {
    // The users module's injector: the picker's slice and runner may not be registered yet (NBO-2508).
    Promise.all([
      import('../crm/users/tabs/companies/companies.component').then(({ CompaniesComponent }) => CompaniesComponent),
      pickerModuleInjector(() => import('../crm/users/users.module').then((m) => m.UsersModule), this.injector)
    ]).then(([CompaniesComponent, injector]) => {
      try {
        const dialogConfig = this.dialog.open(CompaniesComponent, {
          injector,
          width: '75vw',
          height: '75vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          // wCloseBtn zeroes the surface padding the picker frame draws its bands over (NBO-2448).
          panelClass: ['dialog', 'wCloseBtn']
        });

        // CompaniesComponent is configured through @Input()s, and MatDialog does
        // not bind its `data` to inputs — passing the flags there left the picker
        // in its default multi-select shape (a checkbox on every row) while this
        // caller wants exactly one company and closes on the row click. Assign
        // them on the instance, as the CRM Users page does for the same dialog.
        dialogConfig.componentInstance.dialogRef = dialogConfig as any;
        dialogConfig.componentInstance.requestedFromDialog = true;
        dialogConfig.componentInstance.singleValueSelect = true;

        dialogConfig
          .afterClosed()
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              try {
                if (result !== undefined && action.selectedAction === UserTypesPlural.contacts) {
                  this.createUserEntity(action, result.companyId);
                }
              } catch (error) {
                console.error('Error processing dialog result:', error);
                this.errorService.getGqlError(error);
              }
            },
            error: (error) => {
              console.error('Error in dialog afterClosed:', error);
              this.errorService.getGqlError(error);
            }
          });
      } catch (error) {
        console.error('Error opening company selection dialog:', error);
      }
    });
  }

  /**
   * Set loading state
   * @param loading Loading state
   */
  private setLoading(loading: boolean): void {
    setTimeout(() => {
      this.isLoadingSubject.next(loading);
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
